import { stText } from "../i18n/streamingPagesPresentation";
/**
 * devicePlatform -- Shared platform detection for WSD device cards.
 *
 * Provides brand-specific icon resolution, accent color mapping, and
 * platform label derivation from raw platform strings reported by
 * Emby / Jellyfin sessions.
 *
 * Consumed by:
 *   - DevicesPage (Control Center > Devices)
 *   - DevicesTab  (UserAdminPanel > Devices)
 */

// ============================================================================
// Types
// ============================================================================

export type PlatformAccent = "sky" | "emerald" | "violet" | "amber" | "rose" | "slate";

export type PlatformInfo = {
  icon: string;
  label: string;
  accent: PlatformAccent;
  displayKey?: "residualBrowser" | "residualSmartTv" | "unknown";
};

export type AccentColorSet = {
  border: string;
  iconBg: string;
  iconColor: string;
};

// ============================================================================
// Platform detection
// ============================================================================

/**
 * Derive an icon, label, and accent color from platform + device name.
 *
 * The `platform` field stores the upstream session's `Client` value
 * (e.g. "Emby Web", "Jellyfin Web", "Roku", "Android").  For web-based
 * clients the browser identity lives in `deviceName` instead (e.g.
 * "Brave", "Opera", "Chrome").  We combine both fields so that
 * brand-specific detection works regardless of which field carries the
 * distinguishing keyword.
 */
export function detectPlatform(platform: string | null, deviceName?: string | null): PlatformInfo {
  const p = ((platform ?? "") + " " + (deviceName ?? "")).toLowerCase();

  // -- Specific brands first --
  if (p.includes("roku"))
    return { icon: "simple-icons:roku", label: "Roku", accent: "violet" };
  if (p.includes("fire tv") || p.includes("amazon"))
    return { icon: "simple-icons:amazon", label: "Fire TV", accent: "amber" };
  if (p.includes("chromecast"))
    return { icon: "mdi:cast", label: "Chromecast", accent: "violet" };
  if (p.includes("samsung"))
    return { icon: "simple-icons:samsung", label: "Samsung", accent: "violet" };
  if (p.includes("lg"))
    return { icon: "simple-icons:lg", label: "LG", accent: "violet" };
  if (p.includes("playstation") || p.includes("ps4") || p.includes("ps5"))
    return { icon: "simple-icons:playstation", label: "PlayStation", accent: "sky" };
  if (p.includes("xbox"))
    return { icon: "simple-icons:xbox", label: "Xbox", accent: "emerald" };
  if (p.includes("kodi"))
    return { icon: "simple-icons:kodi", label: "Kodi", accent: "sky" };
  if (p.includes("emby theater") || p.includes("emby theatre"))
    return { icon: "simple-icons:emby", label: "Emby Theater", accent: "emerald" };
  if (p.includes("shield") || p.includes("nvidia"))
    return { icon: "simple-icons:nvidia", label: "NVIDIA Shield", accent: "emerald" };
  if (p.includes("raspberry"))
    return { icon: "simple-icons:raspberrypi", label: "Raspberry Pi", accent: "rose" };
  if (p.includes("apple tv") || p.includes("tvos"))
    return { icon: "simple-icons:appletv", label: "Apple TV", accent: "violet" };

  // -- Plex-branded standalone clients (must match before generic OS checks
  //    so "Plex Media Player"/"Plex HTPC" don't fall through to Linux/macOS) --
  if (p.includes("plex media player") || p.includes("plex htpc"))
    return { icon: "simple-icons:plex", label: "Plex Media Player", accent: "amber" };
  if (p.includes("plex for windows") || (p.includes("plex web") && p.includes("windows")))
    return { icon: "simple-icons:plex", label: "Plex Web", accent: "amber" };
  if (p.includes("plex for mac") || (p.includes("plex web") && p.includes("mac")))
    return { icon: "simple-icons:plex", label: "Plex Web", accent: "amber" };

  // -- Generic platform / OS --
  if (p.includes("android"))
    return { icon: "simple-icons:android", label: "Android", accent: "emerald" };
  if (p.includes("iphone") || p.includes("ios") || p.includes("ipad"))
    return { icon: "simple-icons:apple", label: "iOS", accent: "sky" };
  if (p.includes("mac") || p.includes("macos"))
    return { icon: "simple-icons:apple", label: "macOS", accent: "slate" };
  if (p.includes("windows"))
    return { icon: "simple-icons:windows11", label: "Windows", accent: "sky" };
  if (p.includes("linux"))
    return { icon: "simple-icons:linux", label: "Linux", accent: "amber" };

  // -- Browsers --
  if (p.includes("chrome"))
    return { icon: "simple-icons:googlechrome", label: "Chrome", accent: "rose" };
  if (p.includes("brave"))
    return { icon: "simple-icons:brave", label: "Brave", accent: "rose" };
  if (p.includes("firefox"))
    return { icon: "simple-icons:firefox", label: "Firefox", accent: "rose" };
  if (p.includes("safari"))
    return { icon: "simple-icons:safari", label: "Safari", accent: "rose" };
  if (p.includes("edge"))
    return { icon: "simple-icons:microsoftedge", label: "Edge", accent: "rose" };
  if (p.includes("opera"))
    return { icon: "simple-icons:opera", label: "Opera", accent: "rose" };
  if (p.includes("browser"))
    return { icon: "lucide:globe", label: "Browser", displayKey: "residualBrowser", accent: "rose" };

  // -- Generic TV fallback (after specific brands) --
  if (p.includes("tv"))
    return { icon: "lucide:tv", label: "Smart TV", displayKey: "residualSmartTv", accent: "violet" };

  return { icon: "lucide:monitor-smartphone", label: "Unknown", displayKey: "unknown", accent: "slate" };
}

// ============================================================================
// Accent color map
// ============================================================================

export const accentMap: Record<PlatformAccent, AccentColorSet> = {
  sky: {
    border: "border-l-sky-500",
    iconBg: "bg-sky-500/10",
    iconColor: "text-sky-400",
  },
  emerald: {
    border: "border-l-emerald-500",
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
  },
  violet: {
    border: "border-l-violet-500",
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-400",
  },
  amber: {
    border: "border-l-amber-500",
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
  },
  rose: {
    border: "border-l-rose-500",
    iconBg: "bg-rose-500/10",
    iconColor: "text-rose-400",
  },
  slate: {
    border: "border-l-slate-500",
    iconBg: "bg-muted/20",
    iconColor: "text-muted-foreground",
  },
} as const;

/** Preserve the canonical grouping label while resolving authored display copy. */
export const platformDisplayLabel = (info: PlatformInfo): string => info.displayKey ? stText(info.displayKey) : info.label;
